import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { DynamicFields } from "@/components/DynamicFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  MEZZI, TRANSPORT_SCHEMAS, HOTEL_SCHEMA, PARCHEGGIO_SCHEMA, DESTINAZIONE_SCHEMA,
  TRASPORTO_LOCALE_SCHEMA, PRENOTAZIONE_SCHEMA, SERVIZIO_SCHEMA, CONSIGLI_SCHEMA, euro,
} from "@/lib/constants";
import { Plus, Trash2, ChevronRight, ChevronLeft, Save, AlertTriangle, CheckCircle2 } from "lucide-react";

const STEPS = ["Dati", "Trasporti", "Alloggio", "Servizi", "Riepilogo"];

const EMPTY = {
  dipendente: "", azienda: "", responsabile: "", destinazione: "", cliente_sede: "",
  motivo: "", data_partenza: "", ora_partenza: "", data_rientro: "", ora_rientro: "",
  orario_sul_posto: "", numero_persone: 1, note: "", stato: "bozza",
  trasporti: [], hotel: {}, parcheggi: [], destinazione_lavoro: {}, trasporti_locali: [],
  prenotazioni: [], servizi: [], informazioni_consigli: {}, preventivo: {},
};

function SectionTitle({ children }) {
  return <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">{children}</h3>;
}

function CarAvailability({ cars, dettagli, tripStart, tripEnd, excludeTripId, onResult }) {
  const [state, setState] = useState({ loading: false, available: true, conflicts: [] });
  const modello = dettagli?.modello || "";
  let targa = dettagli?.targa || "";
  if (!targa && modello) { const c = cars.find((x) => x.modello === modello); if (c) targa = c.targa; }
  const start = dettagli?.data_utilizzo || tripStart || "";
  const end = tripEnd || start;

  useEffect(() => {
    if ((!targa && !modello) || !start) { onResult(false); setState({ loading: false, available: true, conflicts: [] }); return; }
    let active = true;
    setState((s) => ({ ...s, loading: true }));
    api.get("/company-cars/availability", { params: { targa, modello, start, end, exclude_trip: excludeTripId || "" } })
      .then((r) => { if (!active) return; setState({ loading: false, available: r.data.available, conflicts: r.data.conflicts }); onResult(!r.data.available); })
      .catch(() => { if (!active) return; setState({ loading: false, available: true, conflicts: [] }); onResult(false); });
    return () => { active = false; };
  }, [targa, modello, start, end, excludeTripId]);

  if (!modello && !targa) return null;
  if (state.loading) return <div className="mt-3 text-xs text-slate-400">Verifica disponibilità…</div>;
  if (state.available)
    return <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="car-available"><CheckCircle2 size={14} /> Auto disponibile nelle date indicate</div>;
  return (
    <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="car-conflict">
      <div className="flex items-center gap-2 font-medium"><AlertTriangle size={14} /> Auto già occupata in queste date</div>
      <ul className="mt-1 list-disc pl-5">
        {state.conflicts.map((c) => (<li key={c.trip_id || `${c.targa || c.modello || "auto"}-${c.data_inizio}-${c.data_fine}`}>{c.occupata ? "Auto riservata" : `${c.destinazione} (${c.dipendente})`} · {c.data_inizio} → {c.data_fine}</li>))}
      </ul>
    </div>
  );
}

export default function TripWizard() {
  const { user, activeCompany } = useAuth();
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [trip, setTrip] = useState(EMPTY);
  const [cars, setCars] = useState([]);
  const [saving, setSaving] = useState(false);
  const [carConflicts, setCarConflicts] = useState({});
  const effectiveCompany = activeCompany || user?.active_company || user?.company;
  const isConsolidated = effectiveCompany === "TUTTE";

  useEffect(() => {
    api.get("/company-cars").then((r) => setCars(r.data)).catch(() => setCars([]));
    if (editing) {
      api.get(`/trips/${id}`).then((r) => setTrip({ ...EMPTY, ...r.data })).catch((e) => toast.error(apiError(e)));
    } else {
      setTrip({ ...EMPTY, azienda: effectiveCompany });
    }
  }, [id, editing, effectiveCompany]);

  const set = (k, v) => setTrip((t) => ({ ...t, [k]: v }));

  // --- trasporti ---
  const addSegment = () => set("trasporti", [...trip.trasporti, { id: crypto.randomUUID(), mezzo: "", ordine: trip.trasporti.length, dettagli: {} }]);
  const updateSegment = (idx, patch) => {
    const next = [...trip.trasporti];
    next[idx] = { ...next[idx], ...patch };
    set("trasporti", next);
  };
  const updateSegmentDetail = (idx, key, value) => {
    const next = [...trip.trasporti];
    next[idx] = { ...next[idx], dettagli: { ...next[idx].dettagli, [key]: value } };
    set("trasporti", next);
  };
  const removeSegment = (idx) => {
    const seg = trip.trasporti[idx];
    if (seg) setCarConflicts((m) => { const n = { ...m }; delete n[seg.id]; return n; });
    set("trasporti", trip.trasporti.filter((_, i) => i !== idx));
  };

  // --- generic list (parcheggi, trasporti_locali, prenotazioni, servizi) ---
  const addItem = (field) => set(field, [...trip[field], { id: crypto.randomUUID() }]);
  const updateItem = (field, idx, key, value) => {
    const next = [...trip[field]];
    next[idx] = { ...next[idx], [key]: value };
    set(field, next);
  };
  const removeItem = (field, idx) => set(field, trip[field].filter((_, i) => i !== idx));

  const checkCarConflicts = async () => {
    const out = [];
    for (const seg of trip.trasporti) {
      if (seg.mezzo !== "auto_aziendale") continue;
      let targa = seg.dettagli?.targa || "";
      const modello = seg.dettagli?.modello || "";
      if (!targa && modello) { const c = cars.find((x) => x.modello === modello); if (c) targa = c.targa; }
      const start = seg.dettagli?.data_utilizzo || trip.data_partenza;
      const end = trip.data_rientro || start;
      if ((!targa && !modello) || !start) continue;
      try {
        const r = await api.get("/company-cars/availability", { params: { targa, modello, start, end, exclude_trip: id || "" } });
        if (!r.data.available) out.push(`${modello || targa} → ${r.data.conflicts.map((c) => c.destinazione).join(", ")}`);
      } catch (_) {
        // La disponibilità è opzionale: il salvataggio viene comunque validato dal backend.
      }
    }
    return out;
  };

  const save = async (asDraft) => {
    if (isConsolidated) {
      toast.error("Seleziona COSMA o RIMAR prima di creare o modificare una trasferta.");
      return;
    }
    const conflicts = await checkCarConflicts();
    if (conflicts.length && !asDraft) {
      toast.error("Auto aziendale già occupata negli stessi giorni: " + conflicts.join("; ") + ". Scegli un'altra auto o modifica le date.");
      setStep(1);
      return;
    }
    if (conflicts.length && asDraft) {
      toast.warning("Attenzione: auto aziendale in sovrapposizione. Bozza salvata comunque.");
    }
    setSaving(true);
    const payload = { ...trip, azienda: effectiveCompany };
    const isNewOrDraft = !editing || trip.stato === "bozza";
    if (isNewOrDraft) payload.stato = asDraft ? "bozza" : "richiesta";
    try {
      let res;
      if (editing) res = await api.put(`/trips/${id}`, payload);
      else res = await api.post("/trips", payload);
      toast.success(editing ? "Trasferta aggiornata" : "Trasferta creata");
      navigate(`/trasferte/${res.data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
            {editing ? "Modifica trasferta" : "Nuova trasferta"}
          </h1>
          <Button variant="outline" data-testid="save-draft-btn" onClick={() => save(true)} disabled={saving}>
            <Save size={16} className="mr-1.5" /> Salva bozza
          </Button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 sm:gap-2 mb-8 overflow-x-auto pb-1">
          {STEPS.map((s, i) => (
            <button
              key={s}
              data-testid={`step-${i}`}
              onClick={() => setStep(i)}
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                i === step ? "bg-[#E30613] text-white" : i < step ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"
              }`}
            >
              <span className="h-5 w-5 rounded-full bg-white/20 flex items-center justify-center text-xs">{i + 1}</span>
              {s}
            </button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8">
          {/* STEP 0 - DATI */}
          {step === 0 && (
            <div>
              <SectionTitle>Dati della trasferta</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Dipendente / viaggiatore</Label>
                  <Input data-testid="input-dipendente" value={trip.dipendente} onChange={(e) => set("dipendente", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Azienda di appartenenza</Label>
                  <Input data-testid="input-azienda" value={effectiveCompany || ""} disabled />
                </div>
                <div className="space-y-1.5">
                  <Label>Responsabile / referente</Label>
                  <Input data-testid="input-responsabile" value={trip.responsabile} onChange={(e) => set("responsabile", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Destinazione</Label>
                  <Input data-testid="input-destinazione" value={trip.destinazione} onChange={(e) => set("destinazione", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Cliente / sede / fiera / evento</Label>
                  <Input data-testid="input-cliente" value={trip.cliente_sede} onChange={(e) => set("cliente_sede", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Numero di persone</Label>
                  <Input data-testid="input-persone" type="number" min="1" value={trip.numero_persone} onChange={(e) => set("numero_persone", parseInt(e.target.value) || 1)} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Motivo della trasferta</Label>
                  <Input data-testid="input-motivo" value={trip.motivo} onChange={(e) => set("motivo", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Data di partenza</Label>
                  <Input data-testid="input-data-partenza" type="date" value={trip.data_partenza} onChange={(e) => set("data_partenza", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Ora di partenza</Label>
                  <Input data-testid="input-ora-partenza" type="time" value={trip.ora_partenza} onChange={(e) => set("ora_partenza", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Data di rientro</Label>
                  <Input data-testid="input-data-rientro" type="date" value={trip.data_rientro} onChange={(e) => set("data_rientro", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Ora di rientro</Label>
                  <Input data-testid="input-ora-rientro" type="time" value={trip.ora_rientro} onChange={(e) => set("ora_rientro", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Orario entro cui essere sul posto</Label>
                  <Input data-testid="input-orario-posto" type="time" value={trip.orario_sul_posto} onChange={(e) => set("orario_sul_posto", e.target.value)} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Note / esigenze particolari</Label>
                  <Textarea data-testid="input-note" value={trip.note} onChange={(e) => set("note", e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* STEP 1 - TRASPORTI */}
          {step === 1 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <SectionTitle>Trasporti (segmenti di viaggio)</SectionTitle>
                <Button size="sm" variant="outline" data-testid="add-segment-btn" onClick={addSegment}>
                  <Plus size={15} className="mr-1" /> Aggiungi segmento
                </Button>
              </div>
              {trip.trasporti.length === 0 && <p className="text-sm text-slate-400 mb-4">Nessun segmento. Puoi aggiungere più tratte (es. Parma → Aeroporto → Roma).</p>}
              <div className="space-y-4">
                {trip.trasporti.map((seg, idx) => (
                  <div key={seg.id} className="border border-slate-200 rounded-lg p-4" data-testid={`segment-${idx}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="h-6 w-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center shrink-0">{idx + 1}</span>
                      <div className="flex-1">
                        <Select value={seg.mezzo || undefined} onValueChange={(v) => updateSegment(idx, { mezzo: v })}>
                          <SelectTrigger data-testid={`segment-mezzo-${idx}`}><SelectValue placeholder="Scegli mezzo di trasporto" /></SelectTrigger>
                          <SelectContent>
                            {MEZZI.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <button data-testid={`remove-segment-${idx}`} onClick={() => removeSegment(idx)} className="text-slate-400 hover:text-rose-600">
                        <Trash2 size={17} />
                      </button>
                    </div>
                    {seg.mezzo && TRANSPORT_SCHEMAS[seg.mezzo] && (
                      <DynamicFields
                        schema={TRANSPORT_SCHEMAS[seg.mezzo]}
                        values={seg.dettagli}
                        onChange={(k, v) => updateSegmentDetail(idx, k, v)}
                        cars={cars}
                        idPrefix={`seg${idx}`}
                      />
                    )}
                    {seg.mezzo === "auto_aziendale" && (
                      <CarAvailability
                        cars={cars}
                        dettagli={seg.dettagli}
                        tripStart={trip.data_partenza}
                        tripEnd={trip.data_rientro}
                        excludeTripId={id}
                        onResult={(has) => setCarConflicts((m) => ({ ...m, [seg.id]: has }))}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2 - ALLOGGIO / PARCHEGGIO / DESTINAZIONE */}
          {step === 2 && (
            <div className="space-y-8">
              <div>
                <SectionTitle>Hotel / Pernottamento</SectionTitle>
                <DynamicFields schema={HOTEL_SCHEMA} values={trip.hotel} onChange={(k, v) => set("hotel", { ...trip.hotel, [k]: v })} idPrefix="hotel" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <SectionTitle>Parcheggi</SectionTitle>
                  <Button size="sm" variant="outline" data-testid="add-parking-btn" onClick={() => addItem("parcheggi")}><Plus size={15} className="mr-1" /> Aggiungi</Button>
                </div>
                {trip.parcheggi.map((p, idx) => (
                  <div key={p.id} className="border border-slate-200 rounded-lg p-4 mb-3" data-testid={`parking-${idx}`}>
                    <div className="flex justify-end mb-2"><button onClick={() => removeItem("parcheggi", idx)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16} /></button></div>
                    <DynamicFields schema={PARCHEGGIO_SCHEMA} values={p} onChange={(k, v) => updateItem("parcheggi", idx, k, v)} idPrefix={`park${idx}`} />
                  </div>
                ))}
              </div>
              <div>
                <SectionTitle>Destinazione di lavoro</SectionTitle>
                <DynamicFields schema={DESTINAZIONE_SCHEMA} values={trip.destinazione_lavoro} onChange={(k, v) => set("destinazione_lavoro", { ...trip.destinazione_lavoro, [k]: v })} idPrefix="dest" />
              </div>
            </div>
          )}

          {/* STEP 3 - SERVIZI / PRENOTAZIONI / LOCALE / CONSIGLI */}
          {step === 3 && (
            <div className="space-y-8">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <SectionTitle>Trasporti locali</SectionTitle>
                  <Button size="sm" variant="outline" data-testid="add-local-btn" onClick={() => addItem("trasporti_locali")}><Plus size={15} className="mr-1" /> Aggiungi</Button>
                </div>
                {trip.trasporti_locali.map((p, idx) => (
                  <div key={p.id} className="border border-slate-200 rounded-lg p-4 mb-3" data-testid={`local-${idx}`}>
                    <div className="flex justify-end mb-2"><button onClick={() => removeItem("trasporti_locali", idx)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16} /></button></div>
                    <DynamicFields schema={TRASPORTO_LOCALE_SCHEMA} values={p} onChange={(k, v) => updateItem("trasporti_locali", idx, k, v)} idPrefix={`loc${idx}`} />
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <SectionTitle>Prenotazioni</SectionTitle>
                  <Button size="sm" variant="outline" data-testid="add-booking-btn" onClick={() => addItem("prenotazioni")}><Plus size={15} className="mr-1" /> Aggiungi</Button>
                </div>
                {trip.prenotazioni.map((p, idx) => (
                  <div key={p.id} className="border border-slate-200 rounded-lg p-4 mb-3" data-testid={`booking-${idx}`}>
                    <div className="flex justify-end mb-2"><button onClick={() => removeItem("prenotazioni", idx)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16} /></button></div>
                    <DynamicFields schema={PRENOTAZIONE_SCHEMA} values={p} onChange={(k, v) => updateItem("prenotazioni", idx, k, v)} idPrefix={`book${idx}`} />
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <SectionTitle>Servizi aggiuntivi</SectionTitle>
                  <Button size="sm" variant="outline" data-testid="add-service-btn" onClick={() => addItem("servizi")}><Plus size={15} className="mr-1" /> Aggiungi</Button>
                </div>
                {trip.servizi.map((p, idx) => (
                  <div key={p.id} className="border border-slate-200 rounded-lg p-4 mb-3" data-testid={`service-${idx}`}>
                    <div className="flex justify-end mb-2"><button onClick={() => removeItem("servizi", idx)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16} /></button></div>
                    <DynamicFields schema={SERVIZIO_SCHEMA} values={p} onChange={(k, v) => updateItem("servizi", idx, k, v)} idPrefix={`serv${idx}`} />
                  </div>
                ))}
              </div>
              <div>
                <SectionTitle>Informazioni e consigli</SectionTitle>
                <DynamicFields schema={CONSIGLI_SCHEMA} values={trip.informazioni_consigli} onChange={(k, v) => set("informazioni_consigli", { ...trip.informazioni_consigli, [k]: v })} idPrefix="info" />
              </div>
            </div>
          )}

          {/* STEP 4 - RIEPILOGO */}
          {step === 4 && (
            <div>
              <SectionTitle>Riepilogo</SectionTitle>
              <dl className="grid grid-cols-2 gap-y-2 text-sm mb-6">
                <dt className="text-slate-500">Dipendente</dt><dd className="text-slate-900 font-medium">{trip.dipendente || "—"}</dd>
                <dt className="text-slate-500">Destinazione</dt><dd className="text-slate-900 font-medium">{trip.destinazione || "—"}</dd>
                <dt className="text-slate-500">Partenza</dt><dd className="text-slate-900 font-medium">{trip.data_partenza} {trip.ora_partenza}</dd>
                <dt className="text-slate-500">Rientro</dt><dd className="text-slate-900 font-medium">{trip.data_rientro} {trip.ora_rientro}</dd>
                <dt className="text-slate-500">Segmenti di viaggio</dt><dd className="text-slate-900 font-medium">{trip.trasporti.length}</dd>
                <dt className="text-slate-500">Prenotazioni</dt><dd className="text-slate-900 font-medium">{trip.prenotazioni.length}</dd>
              </dl>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 text-sm text-slate-600" data-testid="workflow-note">
                Salva come <strong>bozza</strong> per continuare a modificare, oppure usa <strong>Invia richiesta di trasferta</strong>: la richiesta passerà all'amministratore che avvierà la ricerca delle soluzioni. Lo stato è gestito automaticamente dal flusso di lavoro.
              </div>
              <Button data-testid="submit-trip-btn" onClick={() => save(false)} disabled={saving} className="bg-[#E30613] hover:bg-[#C50510] font-semibold h-11 px-8">
                {saving ? "Salvataggio..." : (!editing || trip.stato === "bozza") ? "Invia richiesta di trasferta" : "Salva modifiche"}
              </Button>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" data-testid="step-prev" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            <ChevronLeft size={16} className="mr-1" /> Indietro
          </Button>
          {step < STEPS.length - 1 && (
            <Button data-testid="step-next" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} className="bg-slate-900 hover:bg-slate-800">
              Avanti <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
